import { Section } from "react-migration/components/Section";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { ZooplaComparable } from "react-migration/lib/typings/Zoopla";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import { SupportedLocale } from "src/js/util/dates";
import { userStore } from "src/js/stores/user/store";
import { LayerTypeSelectionDetailViewProps } from "../../types";
import { OtherComparablesSection } from "./OtherComparablesSection";
import { ComparableItemDetail } from "react-migration/domains/comparables/components/Results/ComparableItemDetail";
import { useZooplaListingsByUPRN } from "react-migration/domains/comparables/hooks/useZooplaListingsByUPRN";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { useLTComparableByUPRN } from "react-migration/domains/comparables/hooks/useLTComparableByUPRN";
import { useZooplaComparableByUPRNv2 } from "react-migration/domains/comparables/hooks/useZooplaComparableByUPRNv2";
import { getZooplaAccessLevel } from "react-migration/domains/comparables/util/getZooplaAccessLevel";

type ComparablesSelectionDetailViewInnerProps = Readonly<{
  zooplaData: ZooplaComparable;
  zooplaDataLoading: boolean;
  uprn?: string;
}>;

function ComparablesSelectionDetailViewInner({
  zooplaData,
  zooplaDataLoading,
  uprn,
}: ComparablesSelectionDetailViewInnerProps) {
  const locale = userStore.locale as SupportedLocale;
  const unitPreference = getMeasurementSystemPreference();
  const accessLevel = getZooplaAccessLevel();
  const uprnToUse = uprn ? uprn : zooplaData.uprn ? zooplaData.uprn.toString() : "";

  const { data, loading } = useLTComparableByUPRN({
    uprn: uprnToUse,
    skip: !uprnToUse,
  });

  const { data: listingsData, loading: listingsLoading } = useZooplaListingsByUPRN({
    uprn: uprnToUse,
    skip: accessLevel !== "full" || !uprnToUse,
  });

  if (loading) {
    return (
      <div className="relative">
        <SkeletonLoading rows={8} />;
      </div>
    );
  }

  return (
    <>
      <Section data-testid="record">
        <ComparableItemDetail
          record={data}
          recordLoading={loading}
          zooplaData={zooplaData as ZooplaComparable}
          zooplaDataLoading={zooplaDataLoading}
          unitPreference={unitPreference}
          locale={locale}
          listingsData={listingsData}
          listingsLoading={listingsLoading}
          isZoopla={true}
        />
      </Section>
    </>
  );
}

export function HometrackComparableSelectionDetailView({
  selection,
  setDetailSelection,
}: Readonly<LayerTypeSelectionDetailViewProps>) {
  const hasFullAccess = hasFeature(Feature.zooplaFullAccess);

  const { data: zooplaData, loading: zooplaDataLoading } = useZooplaComparableByUPRNv2({
    uprn: selection.id,
    skip: !hasFullAccess,
  });

  if (zooplaDataLoading) {
    return (
      <div className="relative">
        <SkeletonLoading rows={8} />
      </div>
    );
  }

  if (!zooplaData && !hasFullAccess) {
    return <div className="flex flex-col gap-y-1 bg-white">Could not find record</div>;
  }

  return (
    <div className="flex flex-col gap-y-1 bg-white">
      <ComparablesSelectionDetailViewInner
        zooplaData={zooplaData as ZooplaComparable}
        zooplaDataLoading={zooplaDataLoading}
        uprn={selection.id}
      />

      <OtherComparablesSection selection={selection} onSelection={setDetailSelection} />
    </div>
  );
}
